import React, { useState, useMemo } from "react";
import PageHeader from "../../../components/PageHeader";
import useDoableProgress from "../../../hooks/useDoableProgress";
import Postal2FACodeInput from "../Postal2FACodeInput";

// Draft answers win over any staff-app prefill: the client's own last edits
// are the state they expect back.
const applyDraftToConfiguration = (configuration, draft) => {
  const draftFields = draft?.fields;
  if (!draftFields?.length) return configuration;

  const draftFieldsByName = new Map(draftFields.map((field) => [field.fieldName, field]));
  return {
    ...configuration,
    fields: configuration.fields.map((field) => {
      const draftField = draftFieldsByName.get(field.field_identifier);
      if (!draftField) return field;
      return draftField.fieldValues != null
        ? { ...field, values: draftField.fieldValues }
        : { ...field, value: draftField.fieldValue };
    }),
  };
};

const uploadsFromDraft = (draft) => {
  const uploads = {};
  (draft?.uploaded_documents ?? []).forEach(({ identifier, upload_id, filename }) => {
    if (!uploads[identifier]) uploads[identifier] = [];
    uploads[identifier].push({ upload_id, filename });
  });
  return uploads;
};

export const formatSavedAt = (savedAt) => {
  const date = new Date(savedAt);
  if (isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Form renders the loaded form, and must call useDynamicFormDoable with the props it is given
const DynamicFormLoader = ({ doable, caseRef, submissionEnabled, returnToDashboard, Form }) => {
  const [postal2FACode, setPostal2FACode] = useState(null);

  const needs2FACode = !!doable?.configuration.require_postal_2fa_code && !postal2FACode;

  // Drafts are only fetched and saved for client sessions — the progress
  // endpoints reject anyone else — and only once any postal 2FA gate has
  // been passed.
  const draftsEnabled = !!submissionEnabled && !!doable && !needs2FACode;
  const { loadingDraft, draft, savedAt, saveDraft, cancelPendingSave } = useDoableProgress(doable?.id, draftsEnabled);

  const configuration = useMemo(
    () => doable && applyDraftToConfiguration(doable.configuration, draft),
    [doable, draft],
  );
  const initialDocumentUploads = useMemo(() => uploadsFromDraft(draft), [draft]);

  if (!doable) return null;

  if (needs2FACode) {
    return (
      <>
        <PageHeader>Answer questions</PageHeader>
        <Postal2FACodeInput doableID={doable.id} codeValidated={setPostal2FACode} />
      </>
    );
  }

  // The form seeds its state on mount, so hold rendering until we know
  // whether a draft exists.
  if (loadingDraft) {
    return null;
  }

  return (
    <Form
      doable={doable}
      configuration={configuration}
      initialDocumentUploads={initialDocumentUploads}
      postal2FACode={postal2FACode}
      caseRef={caseRef}
      submissionEnabled={submissionEnabled}
      returnToDashboard={returnToDashboard}
      savedAt={savedAt}
      saveDraft={saveDraft}
      cancelPendingSave={cancelPendingSave}
    />
  );
};

export default DynamicFormLoader;
